import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Plus, Trash2, CheckCircle, XCircle, Send } from 'lucide-react';
import { useRequisicaoCompra, useRequisicaoCompraDetalhe } from '@/features/compras/hooks/useRequisicaoCompra';
import type { RequisicaoCompraItemRequest } from '@/features/compras/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';

interface ItemLine extends RequisicaoCompraItemRequest {
  _key: number;
}

const RequisicaoCompraForm = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdicao = Boolean(id && id !== 'nova');
  const idNum = isEdicao ? Number(id) : 0;

  const {
    criarRequisicao, atualizarRequisicao, enviarRequisicao, aprovarRequisicao,
    cancelarRequisicao, isCreating, isUpdating, isEnviando, isAprovando,
  } = useRequisicaoCompra();

  const { data: detalhe } = useRequisicaoCompraDetalhe(idNum);

  const [dtNecessidade, setDtNecessidade] = useState('');
  const [justificativa, setJustificativa] = useState('');
  const [observacao,    setObservacao]    = useState('');
  const [itens,         setItens]         = useState<ItemLine[]>([]);
  const [nextKey,       setNextKey]       = useState(1);

  useEffect(() => {
    if (detalhe) {
      setDtNecessidade(detalhe.dtNecessidade?.substring(0, 10) ?? '');
      setJustificativa(detalhe.justificativa ?? '');
      setObservacao(detalhe.observacao ?? '');
      const linhas: ItemLine[] = detalhe.itens.map((i, idx) => ({
        _key: idx + 1,
        produtoId: i.produtoId,
        descricaoComplementar: i.descricaoComplementar,
        quantidade: i.quantidade,
        unidadeId: i.unidadeId,
        custoEstimado: i.custoEstimado,
        fornecedorSugeridoId: i.fornecedorSugeridoId,
      }));
      setItens(linhas);
      setNextKey(linhas.length + 1);
    }
  }, [detalhe]);

  const addItem = () => {
    setItens(prev => [...prev, { _key: nextKey, produtoId: 0, quantidade: 1, custoEstimado: 0 }]);
    setNextKey(k => k + 1);
  };

  const removeItem = (key: number) => setItens(prev => prev.filter(i => i._key !== key));

  const updateItem = (key: number, field: keyof RequisicaoCompraItemRequest, value: unknown) => {
    setItens(prev => prev.map(i => i._key === key ? { ...i, [field]: value } : i));
  };

  const valorTotal = itens.reduce((s, i) => s + i.quantidade * i.custoEstimado, 0);

  const handleSalvar = async () => {
    const payload = {
      dtNecessidade: dtNecessidade || undefined,
      justificativa,
      observacao,
      itens: itens.map(({ _key: _k, ...rest }) => rest),
    };
    if (isEdicao) {
      await atualizarRequisicao({ id: idNum, data: payload });
    } else {
      const nova = await criarRequisicao(payload);
      navigate(`/compras/requisicoes/${nova.id}`);
      return;
    }
  };

  const podeEditar = !isEdicao || detalhe?.status === 'Rascunho' || detalhe?.status === 'Pendente';
  const isSaving = isCreating || isUpdating;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/compras/requisicoes')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">
              {isEdicao ? `Requisição #${detalhe?.numero ?? ''}` : 'Nova Requisição de Compra'}
            </h1>
            {detalhe && (
              <Badge className={
                detalhe.status === 'Aprovada'   ? 'bg-green-500/10 text-green-600' :
                detalhe.status === 'Pendente'   ? 'bg-yellow-500/10 text-yellow-600' :
                detalhe.status === 'Rejeitada'  ? 'bg-red-500/10 text-red-600' :
                'bg-gray-500/10 text-gray-600'
              }>{detalhe.status}</Badge>
            )}
          </div>
        </div>

        {/* Ações de aprovação */}
        {isEdicao && detalhe?.status === 'Rascunho' && (
          <Button variant="outline" className="gap-2" disabled={isEnviando}
            onClick={() => enviarRequisicao(idNum)}>
            <Send className="h-4 w-4" />Enviar para Aprovação
          </Button>
        )}
        {isEdicao && (detalhe?.status === 'Pendente' || detalhe?.status === 'EmAprovacao') && (
          <div className="flex gap-2">
            <Button variant="outline" className="gap-2 text-green-600 border-green-600"
              disabled={isAprovando}
              onClick={() => aprovarRequisicao({ id: idNum, request: { aprovado: true } })}>
              <CheckCircle className="h-4 w-4" />Aprovar
            </Button>
            <Button variant="outline" className="gap-2 text-red-600 border-red-600"
              disabled={isAprovando}
              onClick={() => aprovarRequisicao({ id: idNum, request: { aprovado: false } })}>
              <XCircle className="h-4 w-4" />Rejeitar
            </Button>
          </div>
        )}
      </div>

      {/* Cabeçalho */}
      <Card>
        <CardHeader><CardTitle>Identificação</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Data de Necessidade</Label>
              <Input type="date" value={dtNecessidade} onChange={e => setDtNecessidade(e.target.value)}
                disabled={!podeEditar} />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Justificativa</Label>
            <Input value={justificativa} onChange={e => setJustificativa(e.target.value)}
              placeholder="Motivo da requisição" disabled={!podeEditar} />
          </div>
          <div className="space-y-2">
            <Label>Observações</Label>
            <Textarea value={observacao} onChange={e => setObservacao(e.target.value)}
              rows={2} disabled={!podeEditar} />
          </div>
        </CardContent>
      </Card>

      {/* Itens */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Itens</CardTitle>
          {podeEditar && (
            <Button size="sm" variant="outline" className="gap-1" onClick={addItem}>
              <Plus className="h-4 w-4" />Adicionar Item
            </Button>
          )}
        </CardHeader>
        <CardContent className="p-0">
          <Table>
<TableHeader>
  <TableRow>
    <TableHead>Produto ID</TableHead>
    <TableHead>Descrição Complementar</TableHead>
    <TableHead className="w-28">Quantidade</TableHead>
    <TableHead className="w-36">Custo Estimado</TableHead>
    <TableHead className="w-36 text-right">Total</TableHead>
    {podeEditar && <TableHead className="w-12" />}
  </TableRow>
</TableHeader>
            <TableBody>
              {itens.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-6 text-muted-foreground">
                    Adicione ao menos um item
                  </TableCell>
                </TableRow>
              ) : (
                itens.map(item => (
                  <TableRow key={item._key}>
                    <TableCell>
                      <Input
                        type="number"
                        value={item.produtoId || ''}
                        onChange={e => updateItem(item._key, 'produtoId', Number(e.target.value))}
                        className="w-24"
                        disabled={!podeEditar}
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        value={item.descricaoComplementar ?? ''}
                        onChange={e => updateItem(item._key, 'descricaoComplementar', e.target.value)}
                        placeholder="Complemento..."
                        disabled={!podeEditar}
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        min="0.0001"
                        step="0.0001"
                        value={item.quantidade}
                        onChange={e => updateItem(item._key, 'quantidade', Number(e.target.value))}
                        disabled={!podeEditar}
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.custoEstimado}
                        onChange={e => updateItem(item._key, 'custoEstimado', Number(e.target.value))}
                        disabled={!podeEditar}
                      />
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      {(item.quantidade * item.custoEstimado).toLocaleString('pt-BR', {
                        style: 'currency', currency: 'BRL',
                      })}
                    </TableCell>
                    {podeEditar && (
                      <TableCell>
                        <Button size="sm" variant="ghost" onClick={() => removeItem(item._key)}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Totais */}
      <div className="flex justify-end">
        <div className="text-right">
          <p className="text-sm text-muted-foreground">Valor Total Estimado</p>
          <p className="text-2xl font-bold">
            {valorTotal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
          </p>
        </div>
      </div>

      {podeEditar && (
        <div className="flex gap-3">
          <Button onClick={handleSalvar} disabled={isSaving || itens.length === 0} className="gap-2">
            <Save className="h-4 w-4" />{isSaving ? 'Salvando...' : 'Salvar'}
          </Button>
          <Button variant="outline" onClick={() => navigate('/compras/requisicoes')}>Cancelar</Button>
          {isEdicao && detalhe?.status === 'Rascunho' && (
            <Button variant="destructive" onClick={() => { cancelarRequisicao(idNum); navigate('/compras/requisicoes'); }}>
              Cancelar Requisição
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default RequisicaoCompraForm;
